import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { companyApi } from "@/services/api";
import { Loader2, TrendingUp, Calendar, HelpCircle } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

interface YieldHistoryEntry {
  date: string;
  dateFormatted: string;
  balanceBrld: number;
  dailyYield: number;
  yieldPercentage: number;
}

interface YieldHistorySummary {
  totalYield: number;
  averageDailyYield: number;
  daysCount: number;
  totalPrincipal: number;
}

export function YieldHistoryTable() {
  const [history, setHistory] = useState<YieldHistoryEntry[]>([]);
  const [summary, setSummary] = useState<YieldHistorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyApi.dashboard.getYieldHistory();
      setHistory(data.history);
      setSummary(data.summary);
    } catch (err) {
      console.error("Erro ao carregar histórico:", err);
      setError("Não foi possível carregar o histórico de rendimentos");
    } finally {
      setLoading(false);
    }
  };

  const formatYieldValue = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  };

  const formatPercentage = (value: number) => {
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }) + "%";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Histórico de Rendimentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Histórico de Rendimentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Histórico de Rendimentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nenhum histórico de rendimento disponível</p>
            <p className="text-sm mt-1">
              O histórico aparecerá aqui após o primeiro dia útil de aplicação
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Histórico de Rendimentos
          </CardTitle>
          {summary && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-success" />
                <span className="text-muted-foreground">Total:</span>
                <span className="font-medium text-success">
                  {formatYieldValue(summary.totalYield)}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-center">
                      <p>
                        Este valor pode diferir do rendimento no card "Aplicado" caso
                        tenha havido resgates ou aplicações no período. O histórico
                        mostra o rendimento diário registrado a cada dia, enquanto o
                        card recalcula em tempo real.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className="text-muted-foreground">
                ({summary.daysCount} dias)
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Desktop table */}
        <div className="hidden md:block border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 font-medium text-sm">Data</th>
                <th className="text-right p-4 font-medium text-sm">Saldo BRLD</th>
                <th className="text-right p-4 font-medium text-sm">Rend. do Dia</th>
                <th className="text-right p-4 font-medium text-sm">% Rend.</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {history.map((entry) => (
                <tr key={entry.date} className="hover:bg-muted/50">
                  <td className="p-4 text-sm font-medium">{entry.dateFormatted}</td>
                  <td className="p-4 text-sm text-right">
                    {formatCurrency(entry.balanceBrld)}
                  </td>
                  <td className="p-4 text-sm text-right font-medium text-success">
                    {formatYieldValue(entry.dailyYield)}
                  </td>
                  <td className="p-4 text-sm text-right text-muted-foreground">
                    {formatPercentage(entry.yieldPercentage)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {history.map((entry) => (
            <div key={entry.date} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{entry.dateFormatted}</span>
                <span className="text-sm">
                  {formatCurrency(entry.balanceBrld)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">Rendimento</p>
                  <span className="font-medium text-success text-sm">
                    {formatYieldValue(entry.dailyYield)}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Taxa</p>
                  <span className="text-muted-foreground text-sm">
                    {formatPercentage(entry.yieldPercentage)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
